export const ctpFormSchema = {
  api: {
    submit: 'ctps',
  },
  payload: {
    includeRawFormData: true,
    casts: {
      formVersionNumber: 'int',
      clinx002Value: 'number',
      cumulativeAmountBilled: 'number',
      currentCLINX002Balance: 'number',
      newCLINX002Balance: 'number',
      proposedSubcontractorPrice: 'number',
    },
    // Transform estimates from form data to API structure
    transform: (data) => {
      const transformed = { ...data };
      const ctpNumber = data.ctpNumber || 'CTP-UNKNOWN';
      
      // Build estimates array from form data
      if (Array.isArray(data.estimates) && data.estimates.length > 0) {
        transformed.estimates = [];
        
        data.estimates.forEach((estimate, index) => {
          // Filter out blank line items (no description and zero cost)
          const validLineItems = (estimate.lineItems || []).filter(item => {
            const hasDescription = item.description && item.description.trim().length > 0;
            const hasCost = (item.totalLineItemEstimate || 0) > 0;
            return hasDescription || hasCost;
          });
          
          if (validLineItems.length > 0) {
            const estimateNumber = String(index + 1).padStart(3, '0');
            transformed.estimates.push({
              estimateType: 'CTPEstimate',
              objectName: `${ctpNumber}-${estimateNumber}`,
              lineItems: validLineItems,
              totalEstimate: estimate.totalEstimate || validLineItems.reduce((sum, item) => sum + (item.totalLineItemEstimate || 0), 0),
            });
          }
        });
        
        // If no valid estimates, remove the field
        if (transformed.estimates.length === 0) {
          delete transformed.estimates;
        }
      }
      
      return transformed;
    },
    // Optional: allow-list to control payload shape strictly
    allow: [
      'associatedLineOfAccounting',
      'awardPIID',
      'clientName',
      'clinx002Value',
      'contractorName',
      'ctpDate',
      'ctpNumber',
      'cumulativeAmountBilled',
      'currentCLINX002Balance',
      'descOfSuppliesOrServices',
      'estimates',
      'formId',
      'formVersionNumber',
      'fromContactId',
      'newCLINX002Balance',
      'projectId',
      'projectName',
      'proposedSubcontractor',
      'proposedSubcontractorPrice',
      'purposeJustification',
      'rawFormData',
      'subject',
      'throughContactId',
      'toContactId',
      'typeofSubcontract',
      'description',
      'dueDate',
    ],
  },
  blocks: [
    // Hidden read-only values fetched from forms API (not rendered)
    {
      type: 'hiddenApi',
      route: 'forms',
      // Find the CTP form record by formType
      filter: { formType: 'Consent To Purchase (CTP) Form' },
      map: {
        formId: 'formId',
        formVersionNumber: 'versionNumber',
      },
    },
    { type: 'heading', label: 'Consent to Purchase (CTP)' },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'ctpNumber', label: 'CTP Number', placeholder: 'CTP-2025-001', required: true, flex: '1 1 300px' },
        { type: 'date', name: 'dueDate', label: 'Due Date', required: false, payloadTransform: (v) => (v && v !== '') ? v : undefined, flex: '0 0 200px' },
      ],
    },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'description', label: 'Description', required: false, multiline: true, minRows: 3 },
      ],
    },
    {
      type: 'group',
      fields: [
        { type: 'date', name: 'ctpDate', label: 'CTP Date', required: true },
      ],
    },
    {
      type: 'group',
      fields: [
        {
          type: 'autocomplete',
          name: 'projectId',
          label: 'Project Name',
          required: true,
          placeholder: 'Search project',
          loadFromApiRoute: 'projects',
          loadFromApiField: 'projects',
          optionLabelKey: 'title',
          optionValueKey: 'projectId',
          mapFields: { projectName: 'title' },
          helperText: 'Select a project (stores ID, displays title).'
        },
        { type: 'text', name: 'awardPIID', label: 'Award PIID Number', required: true, placeholder: 'Award PIID Number' }
      ]
    },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'contractorName', label: 'Contractor Name', placeholder: 'MindPoint Group LLC', required: true },
        { type: 'text', name: 'clientName', label: 'Client Name', placeholder: 'Department of Defense', required: true },
      ],
    },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'associatedLineOfAccounting', label: 'Associated Line of Accounting', placeholder: 'LOA-2025-001', required: true },
        { type: 'text', name: 'clinx002Value', label: 'CLIN X002 Value', placeholder: '5000', required: true },
      ],
    },
    {
      type: 'group',
      fields: [
        {
          type: 'autocomplete',
          name: 'toContactId',
          label: 'TO Contact',
          required: true,
          placeholder: 'First & Last',
          loadFromApiRoute: 'contacts',
          optionLabelKey: 'name',
          optionValueKey: 'contactId',
          mapFields: { toContactName: 'name' }
        },
        {
          type: 'autocomplete',
          name: 'fromContactId',
          label: 'FROM Contact',
          required: true,
          placeholder: 'First & Last',
          loadFromApiRoute: 'contacts',
          optionLabelKey: 'name',
          optionValueKey: 'contactId',
          mapFields: { fromContactName: 'name' }
        }
      ],
    },
    {
      type: 'group',
      fields: [
        {
          type: 'autocomplete',
          name: 'throughContactId',
          label: 'THROUGH Contact',
          required: true,
          placeholder: 'First & Last',
          loadFromApiRoute: 'contacts',
          optionLabelKey: 'name',
          optionValueKey: 'contactId',
          mapFields: { throughContactName: 'name' }
        },
        { type: 'text', name: 'cumulativeAmountBilled', label: 'Cumulative Amount Billed', required: true, placeholder: '15000' },
      ],
    },
    { type: 'group', fields: [ { type: 'text', name: 'currentCLINX002Balance', label: 'Current CLIN X002 Balance', required: true, placeholder: '10000' } ] },
    { type: 'group', fields: [ { type: 'text', name: 'newCLINX002Balance', label: 'New CLIN X002 Balance', required: true, placeholder: '5000' } ] },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'subject', label: 'Subject', placeholder: 'Purchase authorization for equipment', required: true },
      ],
    },
    {
      type: 'group',
      fields: [
        {
          type: 'text',
          name: 'purposeJustification',
          label: 'Purpose/Justification of Request',
          required: true,
          multiline: true,
          placeholder: 'Purchase necessary equipment for project',
        },
      ],
    },
    {
      type: 'group',
      fields: [
        {
          type: 'text',
          name: 'descOfSuppliesOrServices',
          label: 'Description of Supplies or Services',
          required: true,
          multiline: true,
          placeholder: 'Computer equipment and software licenses',
        },
      ],
    },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'typeofSubcontract', label: 'Type of Subcontract', placeholder: 'Fixed Price', required: true },
        { type: 'text', name: 'proposedSubcontractor', label: 'Proposed Subcontractor', placeholder: 'Tech Supplier Inc', required: true },
      ],
    },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'proposedSubcontractorPrice', label: 'Proposed Subcontractor Price', placeholder: '4500', required: true },
      ],
    },
    // Estimated Purchase Cost Section
    {
      type: 'group',
      fields: [
        {
          type: 'estimateTable',
          name: 'estimates',
          label: 'ESTIMATED PURCHASE COST:',
          required: true,
          estimateType: 'CTPEstimate',
          indirectCostLabel: 'Subcontractor',
        },
      ],
    },
    // File attachments - uploaded after successful form submission
    {
      type: 'group',
      fields: [
        {
          type: 'fileUpload',
          name: 'attachments',
          label: 'Attach Supporting Documents',
          objectType: 'CTP',
          helperText: 'Upload any supporting documents (quotes, justifications, etc.). Files will be uploaded after form submission.',
        },
      ],
    },
  ],
};
